import React, { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Dropdown, Row, Breadcrumb, Card, Tab, Nav, Form, Button, Spinner } from 'react-bootstrap';
import { FormSelect } from 'components/elements/form-select/FormSelect';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FileText, ExternalLink, MoreVertical } from 'react-feather';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PaymentElement } from '@stripe/react-stripe-js';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

// Dynamische URL basierend auf der Umgebung
const stripePublicKey = process.env.REACT_APP_ENV === 'development'
	? process.env.REACT_APP_STRIPE_PUBLIC_KEY_DEV
	: process.env.REACT_APP_STRIPE_PUBLIC_KEY_PROD;

const stripePromise = loadStripe(stripePublicKey);

// This component is used to display the billing settings, including payment methods and invoices

const BillingSettings = ({ activeKey }) => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(null); // State for loading

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	const auth = useAuthUser(); // Get the authenticated user data
	const [dataInvoices, setDataInvoices] = useState(null); // State

	// API-Call for Invoices
	const getInvoices = async () => {
		setIsLoading(true);
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Invoices
		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/invoices`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				const filteredInvoices = responseData.filter(inv => inv.status !== 'draft');
				setDataInvoices({ data: filteredInvoices });
				setIsLoading(false);
			})
			.catch((error) => {
				setIsLoading(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	// Stripe Payment - In Entwicklung

	const [isLoadingSetupIntents, setIsLoadingSetupIntents] = useState(null); // State for loading
	const [dataSetupIntents, setDataSetupIntents] = useState(null); // State for SetupIntents
	// API-Call for Get SetupIntent's client secret from the backend
	const getSetupIntents = async () => {
		setIsLoadingSetupIntents(true);
		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		// Invoices
		await axios.get(`${baseURL}/tenants/${auth.tenant_id}/payments/setup_intents`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setDataSetupIntents({ data: responseData });
				setIsLoadingSetupIntents(false);
			})
			.catch((error) => {
				setIsLoadingSetupIntents(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};
	console.log('dataSetupIntents', dataSetupIntents);

	const paymentElementApperance = {
		theme: 'flat',
		variables: { colorPrimaryText: '#262626' }
	};

	const paymentElementOptions = {
		// passing the SetupIntent's client secret
		clientSecret: dataSetupIntents?.data[0]?.client_secret || '',
		// Fully customizable with appearance API.
		layout: {
			type: 'accordion',
			defaultCollapsed: false,
			radios: false,
			spacedAccordionItems: false
		},
		// passing the appearance object
		appearance: paymentElementApperance,
	};

	console.log('paymentElementOptions', paymentElementOptions);


	useEffect(() => {
		if (activeKey === 'billing') {
			getInvoices();
			getSetupIntents();
		}
	}, [activeKey]);


	// Custom Toggle for Dropdown
	const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
		<a
			href=""
			ref={ref}
			onClick={e => {
				e.preventDefault();
				onClick(e);
			}}
			style={{ color: 'inherit', textDecoration: 'none', cursor: 'pointer' }}
		>
			{children}
		</a>
	));

	// ActionMenu Component
	const ActionMenu = (row) => {
		return (
			<Dropdown>
				<Dropdown.Toggle as={CustomToggle}>
					<MoreVertical size="15px" className="text-secondary" />
				</Dropdown.Toggle>
				<Dropdown.Menu align="end">
					<Dropdown.Header>{t('ACTION')}</Dropdown.Header>
					<Dropdown.Item eventKey="2" onClick={() => handleRowClick(row.id, 'show')}>
						<ExternalLink size="15px" className="dropdown-item-icon" /> {t('Show')}
					</Dropdown.Item>
					<Dropdown.Item eventKey="1" onClick={() => handleRowClick(row.id, 'download')}>
						<FileText size="15px" className="dropdown-item-icon" /> {t('Download')}
					</Dropdown.Item>
				</Dropdown.Menu>
			</Dropdown>
		);
	};

	// Handler for ActionMenu actions
	const handleRowClick = (id, action) => {
		if (action === 'show') {
			window.open(dataInvoices.data.find(inv => inv.id === id)?.hosted_invoice_url, '_blank', 'noopener,noreferrer');
		} else if (action === 'download') {
			window.open(dataInvoices.data.find(inv => inv.id === id)?.invoice_pdf, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				{/* payment methods */}
				{/*
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Payment methods')}</h4>
						</Card.Header>
						<Card.Body id="payment-settings">
							{isLoadingSetupIntents ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								dataSetupIntents && dataSetupIntents.data && dataSetupIntents.data.length > 0 && stripePromise ? (
									<Elements stripe={stripePromise} options={paymentElementOptions} appearance={paymentElementApperance}>

										 <PaymentElement />

									</Elements>
								) : (
								<div className="text-center">
									<p>{t('No payment methods available. Please add a payment method to proceed.')}</p>
									<Button variant="primary" onClick={() => getSetupIntents()}>{t('Refresh Payment Methods')}</Button>
								</div>
								)
							)}
						</Card.Body>
					</Card>
				</Col>*/}
				{/* invoices */}
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Invoices')}</h4>
						</Card.Header>
						<Card.Body id="invoices-settings">
							{isLoading ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								<>
									{!dataInvoices || !dataInvoices.data || dataInvoices.data.length === 0 ? (
										<span>{t('Good news: You dont have any invoices yet!')}</span>
									) : (
										<div className="table-responsive">
											<table className="table table-hover align-middle">
												<thead>
													<tr>
														<th>{t('Invoice Number')}</th>
														<th>{t('Date')}</th>
														<th>{t('Customer')}</th>
														<th>{t('Status')}</th>
														<th>{t('Amount')}</th>
														<th>{t('Action')}</th>
													</tr>
												</thead>
												<tbody>
													{dataInvoices.data.map((invoice) => {
														const taxAmount = invoice.total_taxes && invoice.total_taxes[0] && typeof invoice.total_taxes[0].amount === 'number'
															? invoice.total_taxes[0].amount
															: 0;
														const currency = invoice.currency?.toUpperCase() || 'EUR';
														return (
															<tr key={invoice.id}>
																<td>
																	<Link className="fw-semibold" to={invoice.hosted_invoice_url} target="_blank" rel="noopener noreferrer">
																		{invoice.number} <i className="fe fe-arrow-up-right fs-4"></i>
																	</Link>
																</td>
																<td>{new Date(invoice.created * 1000).toLocaleDateString()}</td>
																<td>
																	<div>
																		<div>{invoice.customer_name}</div>
																		<div className="text-muted" style={{ fontSize: '0.85em' }}>{invoice.customer_email}</div>
																	</div>
																</td>
																<td>
																	<span className={`badge bg-${invoice.status === 'paid' ? 'success' : invoice.status === 'open' ? 'warning' : 'secondary'}`}>
																		{t(invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1))}
																	</span>
																</td>
																<td>
																	<div className="">
																		<div>
																			{(taxAmount / 100).toLocaleString(undefined, { style: 'currency', currency })}
																		</div>
																		<small>{t('Tax')}</small>
																	</div>
																	<div className="mt-2">
																		<h5 className="mb-n1">
																			{((invoice.total ?? 0) / 100).toLocaleString(undefined, { style: 'currency', currency })}
																		</h5>
																		<small>{t('Total')}</small>
																	</div>
																</td>
																<td>
																	<ActionMenu id={invoice.id} />
																</td>
															</tr>
														);
													})}
												</tbody>
											</table>
										</div>
									)}
								</>
							)}
						</Card.Body>
						<Card.Footer className="d-flex justify-content-end">
							<small>{t('Not right?')}{' '} <Link target="_blank" to="/marketing/help-center/support" className="text-decoration-none">{t('Contact our Help Desk Team')} <i className="fe fe-arrow-up-right"></i></Link></small>
						</Card.Footer>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);

};

export default BillingSettings;